import {
  ExcelIngestProgressList,
  type ProgressEntry,
} from '@/components/admin/data-sources/ExcelIngestProgressList';
import { ExcelSheetReviewPanel } from '@/components/admin/data-sources/ExcelSheetReviewPanel';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { streamExcelCreate, streamExcelParse } from '@/lib/excelDataSourceStream';
import type {
  CreateExcelDataSourceFromUploadRequest,
  ExcelParseResult,
  ExcelSheetCommit,
} from '@innermate/shared';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileSpreadsheet, Loader2, Upload, UploadCloud } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';

type Step = 'upload' | 'parsing' | 'review' | 'committing' | 'failed';

const SUPPORTED_EXTENSIONS = ['.xlsx', '.xls', '.xlsm', '.xlsb', '.csv', '.psv', '.sam'];

/** 한글/공백 그대로 보존하면서 SQL 식별자로 안전하게 정리 — 줄바꿈/탭/연속공백/큰따옴표 제거. */
function normalizeIdent(raw: string): string {
  return raw
    .replace(/[ -]/g, '') // 제어문자
    .replace(/"/g, '') // 큰따옴표
    .replace(/\s+/g, ' ') // 연속 공백
    .trim();
}

function buildSheetCommitFromParse(p: ExcelParseResult): ExcelSheetCommit[] {
  return p.sheets.map((s) => {
    const tableName = normalizeIdent(s.sheetName) || `sheet_${Math.floor(Math.random() * 1e6)}`;
    return {
      sheetName: s.sheetName,
      include: s.isData,
      tableName: tableName.slice(0, 63),
      columns: s.columns.map((c) => {
        // 영구 SQL 컬럼명 default — raw 헤더 보존, 없으면 임시 sqlName (영문 fallback).
        const fromHeader = c.rawHeader ? normalizeIdent(c.rawHeader) : '';
        const sqlName = (fromHeader.length > 0 ? fromHeader : c.sqlName).slice(0, 63);
        return {
          sqlName,
          parseSqlName: c.sqlName,
          label: c.rawHeader ?? '',
          description: null,
          type: detectedTypeToColumnType(c.detectedType),
          // 데이터 없는 컬럼은 default 로 제외 (사용자가 다시 체크해도 됨).
          include: c.detectedType !== 'empty',
        };
      }),
      pkColumns: [],
      dateColumn: null,
      dateFormat: null,
    };
  });
}

function detectedTypeToColumnType(
  t: ExcelParseResult['sheets'][number]['columns'][number]['detectedType'],
): ExcelSheetCommit['columns'][number]['type'] {
  switch (t) {
    case 'number':
      return 'numeric';
    case 'date':
      return 'timestamptz';
    case 'boolean':
      return 'boolean';
    case 'string':
    case 'mixed':
    case 'empty':
      return 'text';
  }
}

export function AdminDataSourceNewExcel() {
  const navigate = useNavigate();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createSkillEnabled, setCreateSkillEnabled] = useState(true);
  const [skillSlug, setSkillSlug] = useState('');
  const [skillName, setSkillName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileUploadId, setFileUploadId] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<ExcelParseResult | null>(null);
  const [sheetState, setSheetState] = useState<ExcelSheetCommit[]>([]);
  const [entries, setEntries] = useState<ProgressEntry[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  /** dedupKey 가 있으면 같은 entry 갱신, 없으면 새 entry 추가. */
  const upsertEntry = useCallback((entry: Omit<ProgressEntry, 'id'> & { id?: string }) => {
    setEntries((prev) => {
      if (entry.dedupKey) {
        const idx = prev.findIndex((e) => e.dedupKey === entry.dedupKey);
        if (idx >= 0) {
          const next = prev.slice();
          const cur = next[idx];
          if (cur) {
            next[idx] = {
              ...cur,
              kind: entry.kind,
              text: entry.text,
              endedAt: entry.endedAt ?? cur.endedAt,
              startedAt: entry.startedAt ?? cur.startedAt,
            };
          }
          return next;
        }
      }
      const id =
        entry.id ?? `${Date.now()}-${prev.length}-${Math.random().toString(36).slice(2, 6)}`;
      return [...prev, { ...entry, id }];
    });
  }, []);

  const handleFile = (f: File | null) => {
    setFile(f);
    if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
    setFileUploadId(null);
    setParseResult(null);
    setSheetState([]);
    setEntries([]);
    setErrorMsg(null);
  };

  /** 데이터소스명에서 영문 slug 자동 추출. 한글/특수문자는 떨어지므로 비어있으면 사용자가 직접 입력. */
  const slugFromName = useMemo(() => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
  }, [name]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = e.dataTransfer.files?.[0];
      if (!dropped) return;
      const lower = dropped.name.toLowerCase();
      if (!SUPPORTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
        setErrorMsg(`지원하지 않는 확장자입니다: ${dropped.name}`);
        return;
      }
      handleFile(dropped);
    },
    [handleFile],
  );

  const handleStartParse = async () => {
    if (!file) return;
    if (!name.trim()) {
      setErrorMsg('데이터소스 이름을 입력하세요.');
      return;
    }
    setErrorMsg(null);
    setStep('parsing');
    setEntries([]);
    upsertEntry({ kind: 'progress', text: '파일 업로드 중…' });
    try {
      const uploaded = await api.fileUpload(file);
      setFileUploadId(uploaded.id);
      upsertEntry({
        kind: 'progress',
        text: `업로드 완료: ${uploaded.originalName}`,
      });
      const result = await streamExcelParse(uploaded.id, {
        onProgress: (m) => upsertEntry({ kind: 'progress', text: m }),
        onSheetStart: (s) =>
          upsertEntry({
            dedupKey: `parse:${s}`,
            kind: 'sheet_in_progress',
            text: `시트 처리 중: ${s}`,
            startedAt: Date.now(),
          }),
        onSheetDone: ({ sheetName }) =>
          upsertEntry({
            dedupKey: `parse:${sheetName}`,
            kind: 'sheet_done',
            text: `시트 처리 완료: ${sheetName}`,
            endedAt: Date.now(),
          }),
      });
      if (!result.parse) {
        throw new Error('파싱 결과를 받지 못했습니다.');
      }
      setParseResult(result.parse);
      setSheetState(buildSheetCommitFromParse(result.parse));
      upsertEntry({
        kind: 'ingest_done',
        text: `파싱 완료 — 시트 ${result.parse.sheets.length} 개 발견`,
      });
      setStep('review');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setErrorMsg(msg);
      upsertEntry({ kind: 'error', text: msg });
      setStep('failed');
    }
  };

  const validation = useMemo(() => {
    if (!parseResult) return null;
    const includedSheets = sheetState.filter((s) => s.include);
    if (includedSheets.length === 0) {
      return '적어도 한 시트는 포함해야 합니다.';
    }
    for (const s of includedSheets) {
      if (s.tableName.trim().length === 0) return `테이블명이 비었습니다 (${s.sheetName}).`;
      const includedColumns = s.columns.filter((c) => c.include !== false);
      if (includedColumns.length === 0) {
        return `포함된 컬럼이 없습니다 (${s.sheetName}). 한 컬럼 이상 체크하세요.`;
      }
      const seenSqlNames = new Set<string>();
      for (const c of includedColumns) {
        if (c.label.trim().length === 0) {
          return `컬럼 라벨이 비었습니다 (${s.sheetName} → ${c.sqlName}).`;
        }
        if (c.sqlName.trim().length === 0) {
          return `SQL 컬럼명이 비었습니다 (${s.sheetName}).`;
        }
        if (seenSqlNames.has(c.sqlName)) {
          return `중복된 SQL 컬럼명 (${s.sheetName} → ${c.sqlName}).`;
        }
        seenSqlNames.add(c.sqlName);
      }
      if (s.dateColumn && !s.dateFormat) {
        return `날짜키가 있지만 형식이 미설정입니다 (${s.sheetName}).`;
      }
    }
    return null;
  }, [parseResult, sheetState]);

  const handleCommit = async () => {
    if (!fileUploadId || !parseResult) return;
    if (validation) {
      setErrorMsg(validation);
      return;
    }
    setErrorMsg(null);
    setStep('committing');

    // PK 자동 중복 검사 — PK 가 있는 시트별로 임시 테이블에서 사전 검증.
    // 중복이 있으면 commit 단계에서 PRIMARY KEY 충돌로 실패할 것이 명백하므로 미리 차단.
    const sheetsWithPk = sheetState.filter((s) => s.include && s.pkColumns.length > 0);
    if (sheetsWithPk.length > 0) {
      upsertEntry({
        kind: 'progress',
        text: `PK 중복 검사 중… (${sheetsWithPk.length} 시트)`,
      });
      try {
        for (const s of sheetsWithPk) {
          const pkParseColumns = s.pkColumns
            .map((sqlName) => s.columns.find((c) => c.sqlName === sqlName)?.parseSqlName)
            .filter((v): v is string => typeof v === 'string');
          if (pkParseColumns.length === 0) continue;
          const result = await api.dataSourceExcelCheckPkDuplicates({
            fileUploadId,
            sheetName: s.sheetName,
            pkParseColumns,
          });
          if (result.hasDuplicates) {
            const sample = result.samples
              .slice(0, 3)
              .map((x) => `(${x.values.map((v) => v ?? 'NULL').join(', ')}) × ${x.count}`)
              .join(', ');
            const msg = `시트 "${s.sheetName}" 의 PK 컬럼 (${s.pkColumns.join(', ')}) 에 중복이 있어 등록할 수 없습니다 — ${result.totalDuplicateGroups.toLocaleString()} 그룹 (예: ${sample}). PK 를 해제하거나 데이터를 정리한 뒤 다시 시도하세요.`;
            setErrorMsg(msg);
            upsertEntry({ kind: 'ingest_error', text: msg });
            setStep('failed');
            return;
          }
          upsertEntry({
            kind: 'ingest_done',
            text: `${s.sheetName}: PK 중복 없음`,
          });
        }
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message;
        setErrorMsg(`PK 중복 검사 실패: ${msg}`);
        upsertEntry({ kind: 'error', text: `PK 검사 실패: ${msg}` });
        setStep('failed');
        return;
      }
    }

    upsertEntry({ kind: 'progress', text: '데이터소스 등록 시작…' });
    try {
      const skugFinal = (skillSlug.trim() || slugFromName).slice(0, 40);
      const body: CreateExcelDataSourceFromUploadRequest = {
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
        fileUploadId,
        sheets: sheetState,
        createSkill:
          createSkillEnabled && skugFinal.length >= 2
            ? {
                slug: skugFinal,
                name: skillName.trim() || name.trim(),
                description: null,
              }
            : null,
      };
      const result = await streamExcelCreate(body, {
        onProgress: (m) => upsertEntry({ kind: 'progress', text: m }),
        onSheetStart: (s) =>
          upsertEntry({
            dedupKey: `commit:${s}`,
            kind: 'sheet_in_progress',
            text: `시트 적재 중: ${s}`,
            startedAt: Date.now(),
          }),
        onIngestSheetDone: (r) => {
          if (r.error) {
            upsertEntry({
              dedupKey: `commit:${r.sheetName}`,
              kind: 'ingest_error',
              text: `${r.sheetName}: ${r.error}`,
              endedAt: Date.now(),
            });
          } else {
            upsertEntry({
              dedupKey: `commit:${r.sheetName}`,
              kind: 'ingest_done',
              text: `${r.sheetName}: ${r.rowsInserted.toLocaleString()} 행 적재`,
              endedAt: Date.now(),
            });
          }
        },
      });
      if (!result.dataSourceId) {
        throw new Error('데이터소스 ID 를 받지 못했습니다.');
      }
      if (result.skillId) {
        upsertEntry({
          kind: 'ingest_done',
          text: `완료 — 스킬도 자동 생성됨 (${result.skillId.slice(0, 8)}…)`,
        });
      } else if (createSkillEnabled) {
        upsertEntry({
          kind: 'ingest_error',
          text: '스킬 자동 생성은 실패했으나 데이터소스는 정상 등록 — 상세 페이지로 이동합니다.',
        });
      } else {
        upsertEntry({ kind: 'ingest_done', text: '완료 — 상세 페이지로 이동합니다.' });
      }
      navigate({ to: '/admin/data-sources/$id', params: { id: result.dataSourceId } });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setErrorMsg(msg);
      upsertEntry({ kind: 'error', text: msg });
      setStep('failed');
    }
  };

  return (
    <PageShell
      header={
        <>
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/data-sources' })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            데이터 소스 목록
          </button>
          <PageHeader
            title="Excel·CSV 데이터소스 추가"
            description="엑셀(.xlsx/.xls/.xlsb) 또는 CSV 파일을 업로드해 영구 데이터소스로 등록합니다. 시트별로 테이블명·컬럼·PK·날짜키를 검토한 뒤 확정합니다."
          />
        </>
      }
    >
      {errorMsg ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {errorMsg}
        </div>
      ) : null}

      {step === 'upload' || step === 'failed' ? (
        <section className="space-y-3 rounded-2xl border border-border bg-surface px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1 space-y-2">
              <label htmlFor="ds-name" className="text-xs text-foreground-muted">
                데이터소스 이름
              </label>
              <Input
                id="ds-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 2026 매출원장"
              />
            </div>
            <div className="flex-1 space-y-2">
              <label htmlFor="ds-desc" className="text-xs text-foreground-muted">
                설명 (옵션)
              </label>
              <Input
                id="ds-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="이 데이터의 출처/용도"
              />
            </div>
          </div>

          <FileDropZone
            file={file}
            isDragOver={isDragOver}
            onClickPick={() => fileInputRef.current?.click()}
            onDragEnter={() => setIsDragOver(true)}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
          />
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={SUPPORTED_EXTENSIONS.join(',')}
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="sr-only"
          />

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => navigate({ to: '/admin/data-sources' })}>
              취소
            </Button>
            <Button onClick={handleStartParse} disabled={!file || !name.trim()}>
              <Upload className="size-3.5" />
              파일 분석 시작
            </Button>
          </div>
        </section>
      ) : null}

      {step === 'parsing' || step === 'committing' ? (
        <section className="space-y-3 rounded-2xl border border-border bg-surface px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin text-accent" />
            {step === 'parsing' ? '파일 분석 중…' : '데이터소스 등록 중…'}
          </div>
          <ExcelIngestProgressList entries={entries} />
        </section>
      ) : null}

      {step === 'review' && parseResult ? (
        <div className="space-y-4">
          <section className="space-y-2 rounded-2xl border border-border bg-surface px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="size-4 text-accent" />
              파싱 결과 검토 — 시트 {parseResult.sheets.length} 개
            </div>
            <ExcelIngestProgressList entries={entries} />
          </section>

          {parseResult.sheets.map((sheet, idx) => {
            const state = sheetState[idx];
            if (!state) return null;
            return (
              <ExcelSheetReviewPanel
                key={sheet.sheetName}
                sheet={sheet}
                state={state}
                fileUploadId={fileUploadId ?? undefined}
                onChange={(next) => {
                  const arr = sheetState.slice();
                  arr[idx] = next;
                  setSheetState(arr);
                }}
              />
            );
          })}

          <section className="space-y-3 rounded-2xl border border-border bg-surface px-4 py-3">
            <label className="inline-flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={createSkillEnabled}
                onChange={(e) => setCreateSkillEnabled(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">이 데이터로 스킬도 함께 생성</span>
                <span className="ml-1 text-xs text-foreground-muted">
                  (db.* 도구 + 이 데이터소스 화이트리스트로 즉시 사용 가능)
                </span>
              </span>
            </label>
            {createSkillEnabled ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="skill-slug" className="text-xs text-foreground-muted">
                    스킬 slug (영문/숫자/하이픈, 2~40 자)
                  </label>
                  <Input
                    id="skill-slug"
                    value={skillSlug}
                    onChange={(e) => setSkillSlug(e.target.value)}
                    placeholder={slugFromName || 'excel-data'}
                    className="font-mono text-xs"
                  />
                  {skillSlug.trim().length === 0 && slugFromName.length === 0 ? (
                    <p className="text-[11px] text-amber-600">
                      데이터소스명에서 영문 slug 추출 불가 — 직접 입력하세요.
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <label htmlFor="skill-name" className="text-xs text-foreground-muted">
                    스킬 이름 (사용자 챗 화면 표시용)
                  </label>
                  <Input
                    id="skill-name"
                    value={skillName}
                    onChange={(e) => setSkillName(e.target.value)}
                    placeholder={name.trim() || '예: 매출 분석'}
                  />
                </div>
              </div>
            ) : null}
          </section>

          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-foreground-muted">
              {validation
                ? validation
                : `포함될 시트: ${sheetState.filter((s) => s.include).length} / ${sheetState.length}`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('upload');
                  setEntries([]);
                  setParseResult(null);
                }}
              >
                다시 시작
              </Button>
              <Button
                onClick={handleCommit}
                disabled={
                  !!validation ||
                  (createSkillEnabled && (skillSlug.trim() || slugFromName).length < 2)
                }
              >
                데이터소스 만들기
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}

/** 드래그앤드롭 + 클릭 지원 dropzone. 파일이 선택되면 상세 정보 카드. */
export function FileDropZone({
  file,
  isDragOver,
  onClickPick,
  onDragEnter,
  onDragLeave,
  onDrop,
}: {
  file: File | null;
  isDragOver: boolean;
  onClickPick: () => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClickPick}
      onDragEnter={(e) => {
        e.preventDefault();
        onDragEnter();
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        onDragLeave();
      }}
      onDrop={onDrop}
      className={cn(
        'group relative flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
        isDragOver
          ? 'border-accent bg-accent/5'
          : file
            ? 'border-success/40 bg-success/5 hover:bg-success/10'
            : 'border-border bg-surface-muted/20 hover:bg-surface-muted/40',
      )}
    >
      <UploadCloud
        className={cn(
          'size-8 transition-colors',
          isDragOver ? 'text-accent' : file ? 'text-success' : 'text-foreground-muted',
        )}
      />
      {file ? (
        <>
          <p className="text-sm font-medium">{file.name}</p>
          <p className="text-xs text-foreground-muted">
            {(file.size / 1024).toFixed(1)} KB · 클릭하면 다른 파일 선택
          </p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium">파일을 끌어놓거나 클릭해 선택</p>
          <p className="text-xs text-foreground-muted">
            지원: .xlsx · .xls · .xlsm · .xlsb · .csv · .psv · .sam
          </p>
        </>
      )}
    </button>
  );
}
